import React, { useState, useEffect } from 'react';
import { FaUsers, FaUser } from 'react-icons/fa';
import styles from './TeamSelector.module.css';

/**
 * TeamSelector Component
 * Allows selection of either a program team or custom team name
 * 
 * @param {string} position - 'home' or 'away'
 * @param {object} value - Current team value { type, teamId, name }
 * @param {function} onChange - Callback when team selection changes
 * @param {array} teams - Available program teams
 * @param {string} label - Label for the selector
 * @param {boolean} allowCustom - Whether to allow custom team names (default: true)
 */
const TeamSelector = ({ 
  position = 'home', 
  value = { type: 'custom', teamId: null, name: '' }, 
  onChange, 
  teams = [], 
  label = 'Team',
  allowCustom = true 
}) => {
  const [teamType, setTeamType] = useState(value?.type || 'custom');
  const [selectedTeamId, setSelectedTeamId] = useState(value?.teamId || '');
  const [customName, setCustomName] = useState(value?.name || '');

  // Sync with external value changes (when editing event)
  useEffect(() => {
    if (value) {
      setTeamType(value.type || 'custom');
      setSelectedTeamId(value.teamId || '');
      setCustomName(value.name || '');
    }
  }, [value]);

  // Handle team type change (Program Team vs Custom)
  const handleTypeChange = (newType) => {
    setTeamType(newType);
    
    if (newType === 'program_team' && teams.length > 0) {
      // Auto-select first team if available
      const firstTeam = teams[0];
      setSelectedTeamId(firstTeam._id);
      onChange({
        type: 'program_team',
        teamId: firstTeam._id,
        name: firstTeam.name
      });
    } else if (newType === 'custom') {
      setSelectedTeamId('');
      onChange({
        type: 'custom',
        teamId: null,
        name: customName
      });
    }
  };

  // Handle program team selection
  const handleTeamSelect = (e) => {
    const teamId = e.target.value;
    const team = teams.find(t => t._id === teamId);
    
    if (team) {
      setSelectedTeamId(teamId);
      onChange({
        type: 'program_team',
        teamId: team._id,
        name: team.name
      });
    }
  };

  // Handle custom team name input
  const handleCustomNameChange = (e) => {
    const name = e.target.value;
    setCustomName(name);
    onChange({
      type: 'custom',
      teamId: null,
      name: name
    });
  };

  // Get selected team details for display
  const selectedTeam = teams.find(t => t._id === selectedTeamId);

  return (
    <div className={styles.teamSelector}>
      <label className={styles.label}>
        <FaUsers className={styles.labelIcon} />
        {label}
      </label>
      
      {/* Team Type Selection - Only show if teams available and custom allowed */}
      {teams.length > 0 && allowCustom && (
        <div className={styles.teamTypeSelector}>
          <label className={styles.radioLabel}>
            <input 
              type="radio" 
              name={`teamType-${position}`}
              value="program_team"
              checked={teamType === 'program_team'}
              onChange={() => handleTypeChange('program_team')}
              className={styles.radioInput}
            />
            <span className={styles.radioText}>
              <FaUsers className={styles.radioIcon} />
              Program Team
            </span>
          </label>
          
          <label className={styles.radioLabel}>
            <input 
              type="radio" 
              name={`teamType-${position}`}
              value="custom"
              checked={teamType === 'custom'}
              onChange={() => handleTypeChange('custom')}
              className={styles.radioInput}
            />
            <span className={styles.radioText}>
              <FaUser className={styles.radioIcon} />
              Custom Team
            </span>
          </label>
        </div>
      )}

      {/* Team Selection Input */}
      <div className={styles.inputContainer}>
        {teamType === 'program_team' && teams.length > 0 ? (
          <>
            <select 
              value={selectedTeamId} 
              onChange={handleTeamSelect}
              className={styles.teamSelect}
              required
            >
              <option value="">Select a team...</option>
              {teams.map(team => (
                <option key={team._id} value={team._id}>
                  {team.name}
                  {team.coach?.firstName && ` - Coach: ${team.coach.firstName} ${team.coach.lastName}`}
                </option>
              ))}
            </select>

            {/* Team Details Display */}
            {selectedTeam && (
              <div className={styles.teamDetails}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Coach:</span>
                  <span className={styles.detailValue}>
                    {selectedTeam.coach?.firstName} {selectedTeam.coach?.lastName}
                  </span>
                </div>
                
                {selectedTeam.maxPlayers && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Max Players:</span>
                    <span className={styles.detailValue}>{selectedTeam.maxPlayers}</span>
                  </div>
                )}
                
                {selectedTeam.jerseyColors && selectedTeam.jerseyColors.length > 0 && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Colors:</span>
                    <div className={styles.jerseyColors}>
                      {selectedTeam.jerseyColors.map((color, i) => (
                        <span 
                          key={i} 
                          className={styles.colorSwatch}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <input 
            type="text"
            placeholder={`Enter ${position} team name...`}
            value={customName}
            onChange={handleCustomNameChange}
            className={styles.customInput}
            required
          />
        )}
      </div>

      {/* Helpful hint */}
      {teams.length === 0 && (
        <p className={styles.hint}>
          No program teams available. Enter a custom team name.
        </p>
      )}
    </div>
  );
};

export default TeamSelector;














